import LeActivity from "@dsk/icons/LeActivity.vue";
import LeBook from "@dsk/icons/LeBook.vue";
import LeDownload from "@dsk/icons/LeDownload.vue";
import LeLogin from "@dsk/icons/LeLogin.vue";
import LeLogout from "@dsk/icons/LeLogout.vue";
import LePrice from "@dsk/icons/LePrice.vue";
import LeProfile from "@dsk/icons/LeProfile.vue";
import LeSettings from "@dsk/icons/LeSettings.vue";
import { type Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationRaw, useRoute, useRouter } from "vue-router";

import { config } from "@/config";
import { NgModalHelper } from "@/helpers";
import { UserType } from "@/types";

import { useLoginRoute } from "./use-login-route.composable";

export interface MenuItem {
  name: string;
  icon: Component;
  text: string;
  tag: "a" | "button" | "router-link";
  sort: number;
  href?: string | RouteLocationRaw;
  onClick?: () => void;
  platform: "desktop" | "mobile" | "both";
}

export const useHeaderMenuItems = (
  userType: UserType,
  isPaying: boolean,
  isEnterpriseAdmin: boolean,
  isEnterpriseDictionaryEditor: boolean
) => {
  const { t } = useI18n({ useScope: "global" });
  const router = useRouter();
  const route = useRoute();
  const loginRoute = useLoginRoute();

  const isSpaRoute = computed<boolean>(() => route.matched.length > 0);

  // menu items for the dropdown menu
  const MenuItemEnum: Record<string, MenuItem> = {
    // Not logged in
    LogIn: {
      name: "Log in",
      icon: LeLogin,
      text: t("use-header-menu-items.log-in"),
      tag: loginRoute.value.type,
      sort: 1,
      href: loginRoute.value.route,
      platform: "desktop"
    },

    // Logged in
    MyAccount: {
      name: "My account",
      icon: LeProfile,
      text: t("use-header-menu-items.my-account"),
      tag: "button",
      sort: 1,
      onClick: () => {
        NgModalHelper.open("edit-user");
      },
      platform: "desktop"
    },

    MyAccountMobile: {
      name: "My account",
      icon: LeProfile,
      text: t("use-header-menu-items.my-account"),
      tag: "a",
      sort: 1,
      href: "/mobile/user/profile/",
      platform: "mobile"
    },

    Admin: {
      name: "Admin",
      icon: LeSettings,
      text: t("use-header-menu-items.admin"),
      tag: "a",
      sort: 2,
      href: "/enterprise/",
      platform: "both"
    },

    CompanyDictionary: {
      name: "Company dictionary",
      icon: LeBook,
      text: t("use-header-menu-items.editor"),
      tag: "a",
      sort: 2,
      href: "/enterprise/",
      platform: "both"
    },

    CreationAndPricing: {
      name: "Sign-up and prices",
      icon: LePrice,
      text: t("use-header-menu-items.pricing"),
      tag: "a",
      sort: 3,
      href: "/subscription/",
      platform: "both"
    },

    Download: {
      name: "Download",
      icon: LeDownload,
      text: t("use-header-menu-items.download"),
      tag: isSpaRoute.value ? "router-link" : "a",
      sort: 4,
      href: isSpaRoute.value ? { name: "download" } : router.resolve({ name: "download" }).href,
      platform: "both"
    },

    OperatingStatus: {
      name: "Operating status",
      icon: LeActivity,
      text: t("use-header-menu-items.operating-status"),
      tag: "a",
      sort: 5,
      href: "https://drift.ordbogen.com/",
      platform: "both"
    },

    LogOut: {
      name: "Log out",
      icon: LeLogout,
      text: t("use-header-menu-items.log-out"),
      tag: "a",
      sort: 6,
      href: "/logout/",
      platform: "both"
    },

    // Unilogin
    ChangeInstitution: {
      name: "Change institution",
      icon: LeSettings,
      text: t("use-header-menu-items.change-institution"),
      tag: isSpaRoute.value ? "router-link" : "a",
      sort: 2,
      href: "/uni-login/select-institution/",
      platform: "both"
    }
  };

  const menuItems = computed<MenuItem[]>(() => {
    const items = [MenuItemEnum.Download, MenuItemEnum.OperatingStatus];

    if ([UserType.Network, UserType.SchoolNetwork].includes(userType)) {
      items.push(MenuItemEnum.LogIn);
    } else {
      items.push(MenuItemEnum.LogOut);
    }

    if (userType === UserType.Unilogin) {
      items.push(MenuItemEnum.ChangeInstitution);
    }

    if ([UserType.Free, UserType.Private, UserType.Company].includes(userType)) {
      if (config.app.isMobile) {
        items.push(MenuItemEnum.MyAccountMobile);
      } else {
        items.push(MenuItemEnum.MyAccount);
      }
    }

    if (userType === UserType.Free || (userType === UserType.Private && !isPaying)) {
      items.push(MenuItemEnum.CreationAndPricing);
    }

    if (isEnterpriseAdmin) {
      items.push(MenuItemEnum.Admin);
    }

    if (isEnterpriseDictionaryEditor) {
      items.push(MenuItemEnum.CompanyDictionary);
    }

    items.sort((a, b) => a.sort - b.sort);
    return items;
  });

  return { menuItems };
};
